import type { JSX } from 'react';
import styles from './Spinner.module.css';

export type SpinnerSize = 'sm' | 'md' | 'lg';

export interface SpinnerProps {
  /** Size — `sm` (14px), `md` (18px, default), `lg` (24px). */
  size?: SpinnerSize;
  /** Color override. Defaults to currentColor so the spinner inherits its parent. */
  color?: string;
  /** Accessible label announced by screen readers. Defaults to "Loading". */
  label?: string;
  /** Optional extra class to combine with the spinner classes. */
  className?: string;
}

/**
 * Rotating spinner for loading states. Use inside Button to replace the leading
 * icon when `loading` is set; never append. Animation is a constant 0.8s linear
 * rotation per the design system spec.
 *
 * Respects `prefers-reduced-motion` via the global tokens.css media query —
 * animation duration drops to 0ms, so the spinner appears as a static circle.
 */
export function Spinner({
  size = 'md',
  color,
  label = 'Loading',
  className,
}: SpinnerProps): JSX.Element {
  const classes = [styles.spinner, styles[`size_${size}`], className]
    .filter(Boolean)
    .join(' ');

  return (
    <span
      className={classes}
      role="status"
      aria-live="polite"
      aria-label={label}
      style={color ? { color } : undefined}
    >
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle
          cx="12"
          cy="12"
          r="9"
          stroke="currentColor"
          strokeOpacity="0.2"
          strokeWidth="3"
        />
        <path
          d="M21 12a9 9 0 0 0-9-9"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
      <span className={styles.srOnly}>{label}</span>
    </span>
  );
}
